import types from '../mutation-types';
import CampaignAgentApi from '../../api/campaign_agents';

export const state = {
  campaign_agents: [],
  meta: {
    count: 0,
    current_page: 1,
    pageSize: 15,
    reports: {
      success: 0,
      failure: 0,
      unknown: 0,
      total_cost: 0,
      total_duration: 0,
      recipient: 0,
    },
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

// list of getters
const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getMeta(_state) {
    return _state.meta;
  },
  getCampaignAgents(_state) {
    return _state.campaign_agents;
  },
};

// actions list
const actions = {
  createCampaignAgent: async ({ commit }, payload) => {
    commit(types.SET_CAMPAGENT_UI_FLAG, { isCreating: true });
    try {
      const { data } = await CampaignAgentApi.createCampaignAgent(payload);
      commit(types.SET_CAMPAGENT_CREATE, data);
      commit(types.SET_CAMPAGENT_UI_FLAG, { isCreating: false });
      return data;
    } catch (error) {
      console.error('Error creating campaign agent:', error);
      commit(types.SET_CAMPAGENT_UI_FLAG, { isCreating: false });
      throw error;
    }
  },
  fetchCampaignAgent: async (
    { commit, state: currentState },
    query = { page: 1 }
  ) => {
    commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignAgentApi.fetchCampaignAgent(query);
      const newData = response.data?.data || [];
      const meta = response.data?.meta || currentState.meta;

      if (query.page === 1) {
        commit(types.SET_CAMPAGENT_LIST, newData);
      } else {
        const combined = [...currentState.campaign_agents, ...newData];
        commit(types.SET_CAMPAGENT_LIST, combined);
      }
      commit(types.SET_CAMPAGENT_META, meta);

      commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: false });
    } catch (error) {
      console.error('Error fetching campaign agents:', error);
      commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: false });
      throw error;
    }
  },
  fetchCampaignAgentFilterList: async (
    { commit, state: currentState },
    query = { page: 1 }
  ) => {
    commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignAgentApi.fetchCampaignAgentFilterList(
        query
      );
      const newData = response.data?.data || [];
      const meta = response.data?.meta || currentState.meta;

      if (query.page === 1) {
        commit(types.SET_CAMPAGENT_LIST, newData);
      } else {
        const combined = [...currentState.campaign_agents, ...newData];
        commit(types.SET_CAMPAGENT_LIST, combined);
      }
      commit(types.SET_CAMPAGENT_META, meta);

      commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: false });
    } catch (error) {
      console.error('Error fetching campaign agents:', error);
      commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: false });
      throw error;
    }
  },
  deleteCampaignAgent: async ({ commit, state: currentState }, payload) => {
    try {
      const { data } = await CampaignAgentApi.deleteCampaignAgent(payload);
      const updatedList = currentState.campaign_agents.filter(
        item => item.id !== payload
      );
      commit(types.SET_CAMPAGENT_LIST, updatedList);
      return data;
    } catch (error) {
      console.error('Error deleting campaign agent:', error);
      throw error;
    }
  },
  getCampaignAgentDetail: async ({ commit }, id) => {
    commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: true });
    try {
      const { data } = await CampaignAgentApi.getCampaignAgentDetail(id);
      commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: false });
      return data;
    } catch (error) {
      console.error('Error deleting campaign agent:', error);
      commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: false });
      throw error;
    }
  },
  updateCampaignAgent: async ({ commit }, { id, payload }) => {
    commit(types.SET_CAMPAGENT_UI_FLAG, { isUpdating: true });
    try {
      const { data } = await CampaignAgentApi.updateCampaignAgent(id, payload);
      commit(types.SET_CAMPAGENT_UI_FLAG, { isUpdating: false });
      return data;
    } catch (error) {
      console.error('Error updating campaign agent:', error);
      commit(types.SET_CAMPAGENT_UI_FLAG, { isUpdating: false });
      throw error;
    }
  },
  fetchCampaignAudiences: async ({ commit }, { campaignId, params = {} }) => {
    commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignAgentApi.getCampaignAudiences(
        campaignId,
        params
      );
      commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: false });
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign audiences:', error);
      commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: false });
      throw error;
    }
  },
  fetchCampaignAudiencesFilterList: async (
    { commit },
    { campaignId, params = {} }
  ) => {
    commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignAgentApi.getCampaignAudiencesFilterList(
        campaignId,
        params
      );
      commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: false });
      return response.data;
    } catch (error) {
      console.error('Error fetching campaign audiences filter list:', error);
      commit(types.SET_CAMPAGENT_UI_FLAG, { isFetching: false });
      throw error;
    }
  },
};

// list of mutations
const mutations = {
  // mutate UI flags
  [types.SET_CAMPAGENT_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  // create a llmbot
  [types.SET_CAMPAGENT_CREATE]($state, data) {
    $state.campaign_agents.unshift(data); // remove this as the response is different
  },
  [types.SET_CAMPAGENT_META]($state, meta) {
    $state.meta = {
      ...meta,
      count: meta.count || 0,
      current_page: meta.current_page || 1,
      pageSize: meta.pageSize || 15,
    };
  },
  [types.SET_CAMPAGENT_LIST]($state, data) {
    $state.campaign_agents = data;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
